'use client';

import { createRoot, type Root } from 'react-dom/client';

import { createWidgetMount } from '@/shared/utils/widget-mount';

import { UserMenuWidget, type UserMenuLink } from '../components/UserMenuWidget';

const OBSERVED_ATTRIBUTES = [
  'user-name',
  'user-avatar-url',
  'classroom-url',
  'classroom-badge',
  'profile-url',
  'lab-url',
  'logout-url',
];

type UserMenuEventName = 'adipa-user-menu-item-click' | 'adipa-user-menu-logout-click';

function dispatchUserMenuEvent(element: HTMLElement, name: UserMenuEventName, detail?: unknown) {
  element.dispatchEvent(
    new CustomEvent(name, {
      detail,
      bubbles: true,
      composed: true,
    }),
  );
}

export class UserMenuWidgetElement extends HTMLElement {
  static observedAttributes = OBSERVED_ATTRIBUTES;

  private root: Root | null = null;
  private mountNode: HTMLDivElement | null = null;
  private renderTimer: number | null = null;
  private unmountTimer: number | null = null;

  connectedCallback() {
    if (this.unmountTimer !== null) {
      window.clearTimeout(this.unmountTimer);
      this.unmountTimer = null;
    }

    if (!this.mountNode) {
      this.mountNode = createWidgetMount(this, {
        rootAttribute: 'data-adipa-user-menu-root',
      }) as HTMLDivElement;
    }

    if (!this.root) {
      this.root = createRoot(this.mountNode);
    }

    this.scheduleRender();
  }

  disconnectedCallback() {
    if (!this.root || this.unmountTimer !== null) return;

    if (this.renderTimer !== null) {
      window.clearTimeout(this.renderTimer);
      this.renderTimer = null;
    }

    const root = this.root;
    this.unmountTimer = window.setTimeout(() => {
      this.unmountTimer = null;
      if (this.isConnected || this.root !== root) return;
      root.unmount();
      this.root = null;
    }, 0);
  }

  attributeChangedCallback() {
    this.scheduleRender();
  }

  private scheduleRender() {
    if (!this.root || this.renderTimer !== null) return;

    this.renderTimer = window.setTimeout(() => {
      this.renderTimer = null;
      this.renderWidget();
    }, 0);
  }

  private renderWidget() {
    if (!this.root) return;

    this.root.render(
      <UserMenuWidget
        classroomBadge={this.getAttribute('classroom-badge') ?? undefined}
        classroomUrl={this.getAttribute('classroom-url') ?? undefined}
        labUrl={this.getAttribute('lab-url') ?? undefined}
        logoutUrl={this.getAttribute('logout-url') ?? undefined}
        profileUrl={this.getAttribute('profile-url') ?? undefined}
        userAvatarUrl={this.getAttribute('user-avatar-url') ?? undefined}
        userName={this.getAttribute('user-name') ?? undefined}
        onItemClick={(link: UserMenuLink) => {
          dispatchUserMenuEvent(this, 'adipa-user-menu-item-click', {
            href: link.href,
            label: link.label,
          });
        }}
        onLogoutClick={() => {
          dispatchUserMenuEvent(this, 'adipa-user-menu-logout-click');
        }}
      />,
    );
  }
}
